if (!customElements.get('pickup-availability')) {
  customElements.define(
    'pickup-availability',
    class PickupAvailability extends HTMLElement {
      constructor() {
        super();

        // The error markup is captured even when the element starts without the
        // `available` attribute. A variant change can make the element available
        // later and call renderError, which previously appended `undefined`
        // because the constructor had returned before reading the template.
        const template = this.querySelector('template');
        this.errorHtml = template?.content?.firstElementChild?.cloneNode(true) || null;
        this.onClickRefreshList = this.onClickRefreshList.bind(this);

        if (!this.hasAttribute('available')) return;

        this.fetchAvailability(this.dataset.variantId);
      }

      fetchAvailability(variantId) {
        if (!variantId) return;

        let rootUrl = this.dataset.rootUrl;
        if (!rootUrl.endsWith('/')) {
          rootUrl = rootUrl + '/';
        }
        const variantSectionUrl = `${rootUrl}variants/${variantId}/?section_id=pickup-availability`;

        fetch(variantSectionUrl)
          .then((response) => response.text())
          .then((text) => {
            const sectionInnerHTML = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('.shopify-section');
            this.renderPreview(sectionInnerHTML);
          })
          .catch((error) => {
            const button = this.querySelector('button');
            if (button) button.removeEventListener('click', this.onClickRefreshList);
            this.renderError(error);
          });
      }

      onClickRefreshList() {
        this.fetchAvailability(this.dataset.variantId);
      }

      update(variant) {
        if (variant?.available) {
          this.fetchAvailability(variant.id);
        } else {
          this.removeAttribute('available');
          this.innerHTML = '';
        }
      }

      renderError(error) {
        // Surfaced rather than swallowed, so a real network or Liquid fault is
        // still visible while never becoming an uncaught exception.
        if (error) console.warn('Pickup availability could not be loaded.', error);

        this.innerHTML = '';
        if (!this.errorHtml) {
          this.removeAttribute('available');
          return;
        }

        // Cloned each time: appending the stored node would move it out of any
        // previous render and leave nothing to show on the next failure.
        this.appendChild(this.errorHtml.cloneNode(true));

        const button = this.querySelector('button');
        if (button) button.addEventListener('click', this.onClickRefreshList);
      }

      renderPreview(sectionInnerHTML) {
        const existingDrawer = document.querySelector('pickup-availability-drawer');
        if (existingDrawer) existingDrawer.remove();

        const preview = sectionInnerHTML?.querySelector('pickup-availability-preview');
        if (!preview) {
          this.innerHTML = '';
          this.removeAttribute('available');
          return;
        }

        this.innerHTML = preview.outerHTML;
        this.setAttribute('available', '');

        // A shop with no pickup locations returns a preview but no drawer, so
        // every use of the drawer below is conditional on it existing.
        const drawerFragment = sectionInnerHTML.querySelector('pickup-availability-drawer');
        if (drawerFragment) {
          document.body.appendChild(drawerFragment);
          const drawer = document.querySelector('pickup-availability-drawer');
          if (drawer && this.dataset.productPageColorScheme) {
            this.dataset.productPageColorScheme
              .split(' ')
              .filter(Boolean)
              .forEach((colorClass) => drawer.classList.add(colorClass));
          }
        }

        const button = this.querySelector('button');
        if (!button) return;
        if (!drawerFragment) {
          // Nothing to open, so the control is removed rather than left inert.
          button.remove();
          return;
        }
        button.addEventListener('click', (evt) => {
          document.querySelector('pickup-availability-drawer')?.show(evt.target);
        });
      }
    }
  );
}

if (!customElements.get('pickup-availability-drawer')) {
  customElements.define(
    'pickup-availability-drawer',
    class PickupAvailabilityDrawer extends HTMLElement {
      constructor() {
        super();

        this.onBodyClick = this.handleBodyClick.bind(this);

        this.querySelector('button')?.addEventListener('click', () => {
          this.hide();
        });

        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.hide();
        });
      }

      handleBodyClick(evt) {
        const target = evt.target;
        if (
          target != this &&
          !target.closest('pickup-availability-drawer') &&
          target.id != 'ShowPickupAvailabilityDrawer'
        ) {
          this.hide();
        }
      }

      hide() {
        this.removeAttribute('open');
        document.body.removeEventListener('click', this.onBodyClick);
        window.WarmouraScrollLock?.unlock();
        removeTrapFocus(this.focusElement);
      }

      show(focusElement) {
        this.focusElement = focusElement;
        this.setAttribute('open', '');
        document.body.addEventListener('click', this.onBodyClick);
        window.WarmouraScrollLock?.lock();
        trapFocus(this);
      }
    }
  );
}
